'use client';

import { PDFEditorConnector } from '@ethsign/pdf-editor';
import { useEffect, useMemo, useRef, useState } from 'react';

import { ContractEditor } from '@/components/ContractEditorNew';
import { useUserInfo } from '@/hooks';
import { ContractType, ICurrentUser } from '@/types';
import { isSameUser, stringToFile } from '@/utils';

import { LefPanel } from './LefPanel';
type Props = {
  contract: any;
  displayPassword?: string;
  file?: { fileString: string; annotation: string };
  xfdfStrings?: string[];
  showPassword?: (isShow: boolean) => void;
  onLoaded: (instance: any) => void;
  fields: any[];
  onFieldClick: (Id: string, instance: PDFEditorConnector) => void;
  selectedField: string;
  email?: string;
  onFieldsChange: (fields: any) => void;
  onSubmit: (instance: PDFEditorConnector) => void;
  onSelectedFieldChange?: (id: any) => void;
};
export function Sign(props: Props) {
  const [instance, setInstance] = useState<any>();
  const { user } = useUserInfo();
  const pdfEditorRef = useRef<any>(null);
  const fieldsRef = useRef<any>(null);
  const {
    fields,
    contract,
    onFieldClick,
    selectedField,
    file,
    xfdfStrings,
    onFieldsChange,
    onLoaded,
    onSubmit,
    email,
    onSelectedFieldChange
  } = props;
  const currentUser = email ? ({ email } as ICurrentUser) : user!;
  const isShowAnnots = (custom: any) => {
    return custom && (contract?.contractType === ContractType.contract ? isSameUser(currentUser, custom) : true);
  };
  const { fileString, annotation } = file || {};
  const fileObj = useMemo(
    () => fileString && contract?.title && stringToFile(fileString, contract?.title),
    [fileString, contract?.title]
  );
  const isNotSigned = !contract?.recipients
    ?.filter((item: any) => item.role === 'signer' && item.status === 'signed')
    .find((item: any) => item.address === user?.address); // contract?.clientStatus === 'wait_me' || contract?.clientStatus === 'link_active';

  const type = isNotSigned
    ? contract?.contractType === ContractType.link_sign_contract
      ? 'prepareAndSign'
      : 'sign'
    : 'preview';
  useEffect(() => {
    fieldsRef.current = fields;
  }, [fields]);

  useEffect(() => {
    if (!annotation) return;
    const annots = annotation && JSON.parse(annotation);
    const fields: any[] = [];
    annots.forEach((annot: any) => {
      if (isShowAnnots(annot.data) && annot.data?.type !== 'readonlyText') {
        fields.push({ ...annot.data, Id: annot.editorId, PageNumber: annot.pageIndex });
      }
    });
    onFieldsChange && onFieldsChange(fields);
  }, [annotation]);
  useEffect(() => {
    if (contract?.contractType === ContractType.link_sign_contract) {
      onFieldsChange && onFieldsChange([{ type: 'signature', required: true }]);
    }
  }, [contract?.contractType]);

  return (
    <div className="flex flex-row h-full pt-[72px]">
      {contract && (
        <div className="w-[352px]">
          <LefPanel
            dragEnd={(e) => {
              pdfEditorRef.current.dragEnd(e, instance);
            }}
            selectedField={selectedField}
            contract={contract}
            currentUser={currentUser}
            onFieldClick={(Id) => {
              onFieldClick(Id, instance!);
            }}
            onSignatureClick={(data: any) => {
              pdfEditorRef.current?.addAnnotation(data);
            }}
            fields={fields}
            onSubmit={() => onSubmit(instance!)}
          ></LefPanel>
        </div>
      )}
      <div className="flex-1 relative ">
        <ContractEditor
          onSelectedFieldChange={onSelectedFieldChange}
          contractName={contract?.title}
          isMatchUser={contract?.contractType === ContractType.contract}
          isShow={true}
          user={user!}
          currentUser={currentUser}
          file={fileObj}
          xfdfString={annotation}
          xfdfStrings={xfdfStrings}
          ref={pdfEditorRef}
          type={type}
          onFieldsChange={(type, annot) => {
            const fields = fieldsRef.current;
            const newFields = fields.map((item: any) => {
              const newItem = { ...item };
              if (type === 'add') {
                if (newItem.Id === annot?.data.linkId || contract?.contractType === 'link_sign_contract') {
                  newItem.completed = true;
                  newItem.linkId = annot?.id;
                }
              } else if (type === 'delete') {
                if (newItem.Id === annot?.id) {
                  newItem.completed = false;
                  newItem.linkId = '';
                }
              }
              return newItem;
            });
            onFieldsChange && onFieldsChange(newFields);
          }}
          onLoaded={async (instance) => {
            setInstance(instance);
            onLoaded(instance);
          }}
        ></ContractEditor>
      </div>
    </div>
  );
}
